import { useCallback, useEffect, useRef, useState } from "react";
import { DEFAULT_MARKDOWN } from "../defaultMarkdown";
import { EMPTY_META } from "../types/persisted";

const STORAGE_KEY = "vizhacks-mdwriter-document-v1";
const DEBOUNCE_MS = 500;

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data?.v !== 1 || typeof data.markdown !== "string") return null;
    return data;
  } catch {
    return null;
  }
}

function saveToStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

export function usePersistedDocument() {
  const saved = loadFromStorage();
  const [markdown, setMarkdown] = useState(saved?.markdown ?? DEFAULT_MARKDOWN);
  const [showLetterhead, setShowLetterhead] = useState(
    saved?.showLetterhead ?? true,
  );
  const [showPrintPageNumbers, setShowPrintPageNumbers] = useState(
    saved?.showPrintPageNumbers ?? false,
  );
  const [spellCheck, setSpellCheck] = useState(saved?.spellCheck ?? true);
  const [meta, setMeta] = useState(saved?.meta ?? { ...EMPTY_META });
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const debounceRef = useRef(null);

  const persist = useCallback((payload) => {
    saveToStorage(payload);
    setLastSavedAt(Date.now());
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      persist({
        v: 1,
        markdown,
        showLetterhead,
        showPrintPageNumbers,
        spellCheck,
        meta,
      });
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    markdown,
    showLetterhead,
    showPrintPageNumbers,
    spellCheck,
    meta,
    persist,
  ]);

  const clearDraft = useCallback(() => {
    setMarkdown(DEFAULT_MARKDOWN);
    setMeta({ ...EMPTY_META });
    setShowLetterhead(true);
    setShowPrintPageNumbers(false);
    setSpellCheck(true);
    persist({
      v: 1,
      markdown: DEFAULT_MARKDOWN,
      showLetterhead: true,
      showPrintPageNumbers: false,
      spellCheck: true,
      meta: { ...EMPTY_META },
    });
  }, [persist]);

  return {
    markdown,
    setMarkdown,
    showLetterhead,
    setShowLetterhead,
    showPrintPageNumbers,
    setShowPrintPageNumbers,
    spellCheck,
    setSpellCheck,
    meta,
    setMeta,
    clearDraft,
    lastSavedAt,
  };
}
